// Survey Report Summary dashboard (v6.6, enhanced v6.7 2026-05-28).
//
// Top of the admin Survey Report Summary. Provides:
//   1. Four stat tiles (Respondents / Completed / Hostile Comments / Avg Peer Score).
//   2. A per-team respondent table with:
//      - full PLAHER dimension column names (v6.7)
//      - column headers repeated per team block (v6.7; legible with many teams)
//      - color-coded score cells by band (v6.7; red <2.5, amber 2.5-3.5, green >=3.5)
//      - a bold team-average row per team (v6.7)
//      - concern flags per respondent: low peer scores / overestimator (v6.7)
//      - sortable columns (click a header; sorts members within each team) (v6.7)
//      - anchor-jump links to each individual report card below.
//
// Data sources:
//   - respondent.report.ratings (teamPlayerCalc; UNFILTERED -- intentional for
//     the admin's full picture; the recipient-facing report applies the filter).
//   - hostileSubmissions (/api/survey/:id/report/hostile) for Auth/Recv counts.

/* eslint-disable react/no-array-index-key */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge, Card, Spinner, Table,
} from 'react-bootstrap';
import PropTypes from 'prop-types';

import { getSurveyHostileSubmissions } from '../../../bridge';
import { logError } from '../../../helpers/logger';

const PLAHER_DIMS = ['Performance', 'Leadership', 'Attitude', 'Harmony', 'Expression', 'Reliable'];

const fmt1 = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(1) : '—';
};
const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const mean = (xs) => {
  const v = xs.filter((x) => x !== null && x !== undefined).map(Number).filter((x) => Number.isFinite(x));
  return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
};
const safeArr = (v) => (Array.isArray(v) ? v : []);

// Heatmap band -> cell background. 3 bands keep it readable across many teams.
const bandColor = (v) => {
  const n = num(v);
  if (n === null) return undefined;
  if (n >= 3.5) return '#d8efda'; // green  (Strong / Outstanding)
  if (n >= 2.5) return '#fcefcf'; // amber  (Moderate)
  return '#f8d8d8'; // red (Bare Minimum / Unsatisfactory)
};

// Per-respondent concern flags from the (unfiltered) ratings.
function concernFlags(respondent) {
  const ratings = (respondent.report && respondent.report.ratings) || {};
  const peers = PLAHER_DIMS.map((d) => num(ratings[d] && ratings[d].peer)).filter((x) => x !== null);
  const selfs = PLAHER_DIMS.map((d) => num(ratings[d] && ratings[d].self)).filter((x) => x !== null);
  const flags = [];
  if (peers.length) {
    const avgPeer = mean(peers);
    if (avgPeer !== null && avgPeer < 2.5) flags.push({ kind: 'low', text: 'low peer scores' });
    if (selfs.length) {
      const avgSelf = mean(selfs);
      if (avgSelf !== null && avgPeer !== null && avgSelf - avgPeer >= 1.0) {
        flags.push({ kind: 'over', text: `overestimator (self ${avgSelf.toFixed(1)} / peers ${avgPeer.toFixed(1)})` });
      }
    }
  }
  return flags;
}

function teamAverages(members) {
  const avg = {};
  PLAHER_DIMS.forEach((d) => {
    avg[d] = mean(members.map((m) => num(m.report && m.report.ratings && m.report.ratings[d] && m.report.ratings[d].peer)));
  });
  return avg;
}

function StatTile({
  label, value, sublabel, href,
}) {
  const inner = (
    <div className="text-center py-3 px-2 h-100 d-flex flex-column justify-content-center">
      <div className="text-muted small text-uppercase" style={{ letterSpacing: '0.06em' }}>{label}</div>
      <div style={{ fontSize: '2rem', fontWeight: 600, lineHeight: 1.1 }}>{value}</div>
      {sublabel ? <div className="text-muted small">{sublabel}</div> : null}
    </div>
  );
  if (href) {
    return <a href={href} className="text-decoration-none text-dark d-block h-100" style={{ cursor: 'pointer' }}>{inner}</a>;
  }
  return inner;
}
StatTile.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  sublabel: PropTypes.node,
  href: PropTypes.string,
};
StatTile.defaultProps = { sublabel: null, href: null };

// A reusable column-header row (repeated per team). Clicking a header sorts
// members within each team by that key.
function HeaderRow({ sort, onSort }) {
  const arrow = (key) => {
    if (sort.key !== key) return '';
    return sort.dir === 'asc' ? ' ▲' : ' ▼';
  };
  const th = (key, label, extra) => (
    <th
      onClick={() => onSort(key)}
      style={{ cursor: 'pointer', whiteSpace: 'nowrap', ...(extra || {}) }}
      title="Click to sort within each team"
    >
      {label}
      {arrow(key)}
    </th>
  );
  return (
    <tr className="text-muted small">
      {th('name', 'Name')}
      {PLAHER_DIMS.map((d) => (
        <React.Fragment key={`h-${d}`}>{th(d, d, { textAlign: 'center' })}</React.Fragment>
      ))}
      {th('auth', 'Authored hostile', { textAlign: 'center' })}
      {th('recv', 'Received hostile', { textAlign: 'center' })}
      <th aria-label="Jump to report" />
    </tr>
  );
}
HeaderRow.propTypes = { sort: PropTypes.object.isRequired, onSort: PropTypes.func.isRequired };

export function SurveyReportDashboard({ respondents, survey }) {
  const [hostileItems, setHostileItems] = useState(null);
  const [hostileLoading, setHostileLoading] = useState(false);
  const [hostileErr, setHostileErr] = useState('');
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });

  const surveyId = survey && survey.surveyId;

  useEffect(() => {
    if (!surveyId) return undefined;
    let cancelled = false;
    setHostileLoading(true);
    setHostileErr('');
    getSurveyHostileSubmissions(surveyId)
      .then(({ response }) => { if (!cancelled) setHostileItems(safeArr(response && response.hostile)); })
      .catch((err) => {
        if (cancelled) return;
        logError('SurveyReportDashboard.getSurveyHostileSubmissions', err);
        setHostileErr('Unable to load hostile-comment counts.');
        setHostileItems([]);
      })
      .finally(() => { if (!cancelled) setHostileLoading(false); });
    return () => { cancelled = true; };
  }, [surveyId]);

  const hostilityByRespondent = useMemo(() => {
    const tally = new Map();
    (hostileItems || []).forEach((item) => {
      const raterId = item.rater && item.rater.respondentId;
      const targetId = item.target && item.target.respondentId;
      if (raterId) { const c = tally.get(raterId) || { authored: 0, received: 0 }; c.authored += 1; tally.set(raterId, c); }
      if (targetId) { const c = tally.get(targetId) || { authored: 0, received: 0 }; c.received += 1; tally.set(targetId, c); }
    });
    return tally;
  }, [hostileItems]);

  const {
    teamGroups, totalResp, completedResp, avgPeerScore,
  } = useMemo(() => {
    const teams = new Map();
    let allScores = 0; let allScoresN = 0; let completed = 0;
    (respondents || []).forEach((r) => {
      const teamKey = r.team || '(no team)';
      if (!teams.has(teamKey)) teams.set(teamKey, []);
      teams.get(teamKey).push(r);
      if (r.status === 'completed') completed += 1;
      const ratings = r.report && r.report.ratings;
      if (ratings) {
        PLAHER_DIMS.forEach((d) => {
          const peer = num(ratings[d] && ratings[d].peer);
          if (peer !== null) { allScores += peer; allScoresN += 1; }
        });
      }
    });
    const sortedTeams = Array.from(teams.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([teamKey, mem]) => ({ team: teamKey, members: mem }));
    return {
      teamGroups: sortedTeams,
      totalResp: (respondents || []).length,
      completedResp: completed,
      avgPeerScore: allScoresN ? (allScores / allScoresN) : null,
    };
  }, [respondents]);

  const onSort = (key) => setSort((prev) => (prev.key === key
    ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
    : { key, dir: key === 'name' ? 'asc' : 'desc' }));

  const sortMembers = (members) => {
    const arr = members.slice();
    const { key, dir } = sort;
    const val = (m) => {
      if (key === 'name') return `${m.lastName || ''} ${m.firstName || ''}`.trim().toLowerCase();
      if (key === 'auth' || key === 'recv') {
        const h = hostilityByRespondent.get(m.respondentId) || { authored: 0, received: 0 };
        return key === 'auth' ? h.authored : h.received;
      }
      const v = num(m.report && m.report.ratings && m.report.ratings[key] && m.report.ratings[key].peer);
      return v === null ? -Infinity : v;
    };
    arr.sort((a, b) => {
      const va = val(a); const vb = val(b);
      if (va < vb) return dir === 'asc' ? -1 : 1;
      if (va > vb) return dir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  };

  const hostileTotal = (hostileItems || []).length;
  const colSpan = PLAHER_DIMS.length + 4; // name + 6 dims + auth + recv + jump

  return (
    <Card className="pagebreak py-3 my-3" id="dashboard">
      <Card.Body>
        <h3 className="mb-3">Dashboard</h3>

        <div className="row g-2 mb-4">
          <div className="col-6 col-md-3"><div className="border rounded h-100"><StatTile label="Respondents" value={totalResp} /></div></div>
          <div className="col-6 col-md-3"><div className="border rounded h-100"><StatTile label="Completed" value={`${completedResp} / ${totalResp}`} /></div></div>
          <div className="col-6 col-md-3">
            <div className="border rounded h-100" style={{ borderColor: hostileTotal > 0 ? '#dc3545' : undefined, borderWidth: hostileTotal > 0 ? 2 : 1 }}>
              <StatTile
                label="Hostile Comments"
                value={hostileLoading ? <Spinner size="sm" animation="border" /> : hostileTotal}
                sublabel={hostileTotal > 0 ? 'Jump to list' : 'None flagged'}
                href={hostileTotal > 0 ? '#flagged-comments' : null}
              />
            </div>
          </div>
          <div className="col-6 col-md-3"><div className="border rounded h-100"><StatTile label="Avg Peer Score" value={fmt1(avgPeerScore)} sublabel="across PLAHER dims" /></div></div>
        </div>

        {hostileErr ? <p className="text-muted small">{hostileErr}</p> : null}

        <h4 className="mb-2">Respondents</h4>
        <p className="text-muted small mb-1">
          Click a respondent&apos;s name to jump to their individual report below.
          Click any column header to sort members within each team. Cells are
          colored by score band (green strong, amber moderate, red low).
          Hostile counts show comments the respondent authored / received that
          the AI safety screen flagged.
        </p>
        <Table responsive size="sm" className="align-middle">
          <tbody>
            {teamGroups.map(({ team, members }) => {
              const avg = teamAverages(members);
              const sorted = sortMembers(members);
              return (
                <React.Fragment key={`team-${team}`}>
                  <tr className="table-dark">
                    <td colSpan={colSpan}>
                      <strong>{team ? `Team ${team}` : 'No team'}</strong>
                      <span className="ms-2 small">{`${members.length} respondent${members.length === 1 ? '' : 's'}`}</span>
                    </td>
                  </tr>
                  {/* Repeated column header per team block (v6.7). */}
                  <HeaderRow sort={sort} onSort={onSort} />
                  {/* Team-average row (v6.7). */}
                  <tr style={{ fontWeight: 600, background: '#eef2f7' }}>
                    <td>Team average</td>
                    {PLAHER_DIMS.map((d) => (
                      <td key={`avg-${team}-${d}`} className="text-center" style={{ background: bandColor(avg[d]) }}>{fmt1(avg[d])}</td>
                    ))}
                    <td className="text-center">&mdash;</td>
                    <td className="text-center">&mdash;</td>
                    <td aria-label="no jump for team average" />
                  </tr>
                  {sorted.map((r) => {
                    const ratings = (r.report && r.report.ratings) || {};
                    const host = hostilityByRespondent.get(r.respondentId) || { authored: 0, received: 0 };
                    const fullName = `${r.firstName || ''} ${r.lastName || ''}`.trim() || r.email || r.respondentId;
                    const flags = concernFlags(r);
                    return (
                      <tr key={r.respondentId}>
                        <td>
                          <a href={`#respondent-${r.respondentId}`} className="text-decoration-none">{fullName}</a>
                          <div className="text-muted small">{r.email}</div>
                          {flags.map((f) => (
                            <div key={`${r.respondentId}-${f.kind}`} className="small" style={{ color: f.kind === 'low' ? '#b02a37' : '#9a6700' }}>
                              {'⚠ '}
                              {f.text}
                            </div>
                          ))}
                        </td>
                        {PLAHER_DIMS.map((d) => {
                          const cell = ratings[d] || {};
                          return (
                            <td key={`${r.respondentId}-${d}`} className="text-center" style={{ background: bandColor(cell.peer) }}>
                              {fmt1(cell.peer)}
                            </td>
                          );
                        })}
                        <td className="text-center">{host.authored > 0 ? <Badge bg="danger">{host.authored}</Badge> : <span className="text-muted">0</span>}</td>
                        <td className="text-center">{host.received > 0 ? <Badge bg="warning" text="dark">{host.received}</Badge> : <span className="text-muted">0</span>}</td>
                        <td className="text-end"><a href={`#respondent-${r.respondentId}`} className="small text-decoration-none">Jump &rsaquo;</a></td>
                      </tr>
                    );
                  })}
                  <tr><td colSpan={colSpan} aria-label="spacer" style={{ border: 'none', height: 8 }} /></tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
        <p className="text-muted small">
          Concern flags: &ldquo;low peer scores&rdquo; = average peer rating under 2.5;
          &ldquo;overestimator&rdquo; = self-rating at least 1.0 point above the peer
          average (where self-ratings were collected).
        </p>
      </Card.Body>
    </Card>
  );
}

SurveyReportDashboard.propTypes = {
  respondents: PropTypes.array,
  survey: PropTypes.object,
};
SurveyReportDashboard.defaultProps = { respondents: [], survey: {} };

export default SurveyReportDashboard;
